/**
 * Admin Hero Slides Page
 *
 * Manage hero carousel slides.
 */

import type { FormEvent } from 'react';
import { useState, useEffect } from 'react';
import { useAdminApi } from '../../hooks/useAdminApi';
import type { HeroSlide } from '@petmas/shared';

interface SlideFormData {
  imageUrl: string;
  imageUrlMobile: string;
  title: string;
  subtitle: string;
  linkUrl: string;
  linkText: string;
  backgroundColor: string;
  textColor: string;
  isActive: boolean;
}

const emptySlide: SlideFormData = {
  imageUrl: '',
  imageUrlMobile: '',
  title: '',
  subtitle: '',
  linkUrl: '',
  linkText: '',
  backgroundColor: '',
  textColor: '#ffffff',
  isActive: true,
};

export function AdminHeroSlidesPage() {
  const {
    getHeroSlides,
    createHeroSlide,
    updateHeroSlide,
    deleteHeroSlide,
    isLoading,
    error: apiError,
  } = useAdminApi();

  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [editingSlide, setEditingSlide] = useState<HeroSlide | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<SlideFormData>(emptySlide);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSlides();
  }, []);

  const loadSlides = async () => {
    const data = await getHeroSlides();
    setSlides(data);
  };

  const handleCreate = () => {
    setEditingSlide(null);
    setFormData(emptySlide);
    setIsCreating(true);
  };

  const handleEdit = (slide: HeroSlide) => {
    setEditingSlide(slide);
    setFormData({
      imageUrl: slide.imageUrl,
      imageUrlMobile: slide.imageUrlMobile ?? '',
      title: slide.title ?? '',
      subtitle: slide.subtitle ?? '',
      linkUrl: slide.linkUrl ?? '',
      linkText: slide.linkText ?? '',
      backgroundColor: slide.backgroundColor ?? '',
      textColor: slide.textColor,
      isActive: slide.isActive,
    });
    setIsCreating(true);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingSlide(null);
    setFormData(emptySlide);
    setError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.imageUrl) {
      setError('La URL de la imagen es requerida');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const slideData = {
        imageUrl: formData.imageUrl,
        imageUrlMobile: formData.imageUrlMobile || undefined,
        title: formData.title || undefined,
        subtitle: formData.subtitle || undefined,
        linkUrl: formData.linkUrl || undefined,
        linkText: formData.linkText || undefined,
        backgroundColor: formData.backgroundColor || undefined,
        textColor: formData.textColor,
        isActive: formData.isActive,
      };

      if (editingSlide) {
        await updateHeroSlide(editingSlide.id, slideData);
      } else {
        await createHeroSlide(slideData);
      }

      await loadSlides();
      handleCancel();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!globalThis.confirm('¿Estas seguro de eliminar este slide?')) return;

    try {
      await deleteHeroSlide(id);
      await loadSlides();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar');
    }
  };

  const handleToggleActive = async (slide: HeroSlide) => {
    try {
      await updateHeroSlide(slide.id, { isActive: !slide.isActive });
      await loadSlides();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al actualizar');
    }
  };

  if (isLoading && slides.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Hero Slides</h2>
          <p className="text-gray-600">Gestiona los banners del carrusel principal</p>
        </div>
        {!isCreating && (
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600"
          >
            + Nuevo Slide
          </button>
        )}
      </div>

      {(error ?? apiError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error ?? apiError}
        </div>
      )}

      {/* Create/Edit Form */}
      {isCreating && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingSlide ? 'Editar Slide' : 'Nuevo Slide'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL de Imagen (Desktop) *
                </label>
                <input
                  type="url"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData((p) => ({ ...p, imageUrl: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL de Imagen (Movil)
                </label>
                <input
                  type="url"
                  value={formData.imageUrlMobile}
                  onChange={(e) => setFormData((p) => ({ ...p, imageUrlMobile: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titulo
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
                  placeholder="Titulo del slide"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subtitulo
                </label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData((p) => ({ ...p, subtitle: e.target.value }))}
                  placeholder="Subtitulo o descripcion"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL de Enlace
                </label>
                <input
                  type="text"
                  value={formData.linkUrl}
                  onChange={(e) => setFormData((p) => ({ ...p, linkUrl: e.target.value }))}
                  placeholder="/perros o https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Texto del Enlace
                </label>
                <input
                  type="text"
                  value={formData.linkText}
                  onChange={(e) => setFormData((p) => ({ ...p, linkText: e.target.value }))}
                  placeholder="Ver productos"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color de Fondo
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.backgroundColor || '#ffffff'}
                    onChange={(e) => setFormData((p) => ({ ...p, backgroundColor: e.target.value }))}
                    className="h-10 w-16 rounded border border-gray-300"
                  />
                  <input
                    type="text"
                    value={formData.backgroundColor}
                    onChange={(e) => setFormData((p) => ({ ...p, backgroundColor: e.target.value }))}
                    placeholder="#ffffff"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color del Texto
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.textColor}
                    onChange={(e) => setFormData((p) => ({ ...p, textColor: e.target.value }))}
                    className="h-10 w-16 rounded border border-gray-300"
                  />
                  <input
                    type="text"
                    value={formData.textColor}
                    onChange={(e) => setFormData((p) => ({ ...p, textColor: e.target.value }))}
                    placeholder="#ffffff"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>
            </div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData((p) => ({ ...p, isActive: e.target.checked }))}
                className="h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded"
              />
              <span className="text-gray-700">Activo</span>
            </label>

            <div className="flex gap-3 justify-end pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-4 py-2 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 disabled:opacity-50"
              >
                {isSaving ? 'Guardando...' : editingSlide ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Slides List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {slides.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>No hay slides configurados</p>
            <p className="text-sm mt-2">Se mostrara el hero por defecto</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {slides.map((slide, index) => (
              <div key={slide.id} className="p-4 flex items-center gap-4">
                {/* Order Number */}
                <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded text-gray-500 text-sm font-medium">
                  {index + 1}
                </div>

                {/* Thumbnail */}
                <div className="w-24 h-16 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                  <img
                    src={slide.imageUrl}
                    alt={slide.title ?? 'Slide'}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {slide.title || 'Sin titulo'}
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    {slide.subtitle || slide.linkUrl || 'Sin contenido'}
                  </p>
                </div>

                {/* Status */}
                <div>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                      slide.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {slide.isActive ? 'Activo' : 'Inactivo'}
                  </span>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleToggleActive(slide)}
                    className="p-2 text-gray-400 hover:text-gray-600"
                    title={slide.isActive ? 'Desactivar' : 'Activar'}
                  >
                    {slide.isActive ? <EyeIcon className="w-5 h-5" /> : <EyeOffIcon className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => handleEdit(slide)}
                    className="p-2 text-gray-400 hover:text-orange-500"
                    title="Editar"
                  >
                    <EditIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(slide.id)}
                    className="p-2 text-gray-400 hover:text-red-500"
                    title="Eliminar"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Icons
function EyeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}

function EyeOffIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  );
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}
